import ClearRounded from "@mui/icons-material/ClearRounded";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import styled from "styled-components";

import {
  ANIMATION,
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
} from "../../../../utils/constants";
import { BaseButton } from "../../../../components/Button";

// The filter bar is control chrome, not a content card. It stays transparent
// and borderless so it reads as subordinate to the Hero summary above and the
// data sections below. A tight top margin groups it under the Hero; a full
// page-margin bottom keeps generous separation before the content sections.
export const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  margin: ${SPACING.sm}px ${SPACING_RESPONSIVE.mobile.pageMargin}px
    ${SPACING_RESPONSIVE.mobile.pageMargin}px;
  padding: 0 ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  overflow: visible;

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING.sm}px ${SPACING_RESPONSIVE.tablet.pageMargin}px
      ${SPACING_RESPONSIVE.tablet.pageMargin}px;
    padding: 0 ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    margin: ${SPACING.sm}px ${SPACING_RESPONSIVE.desktop.pageMargin}px
      ${SPACING_RESPONSIVE.desktop.pageMargin}px;
    padding: 0 ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const PeriodRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas:
    "label count"
    "control control";
  align-items: center;
  gap: ${SPACING.sm + SPACING.xs}px ${SPACING.md}px;

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: auto auto 1fr;
    grid-template-areas: "label control count";
    gap: ${SPACING.md}px;
  }
`;

export const PeriodLabel = styled.span`
  grid-area: label;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

export const Count = styled.span`
  grid-area: count;
  justify-self: end;
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: ${SPACING.xs}px ${SPACING.sm + SPACING.xs}px;
  border: 1px solid var(--color-border-light);
  border-radius: ${SPACING.sm}px;
  background: var(--color-background-0);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-muted);
  white-space: nowrap;
`;

export const PeriodControl = styled.div`
  grid-area: control;
`;

export const PeriodControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

export const SegGroup = styled.div`
  display: inline-flex;
  max-width: 100%;
  overflow-x: auto;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.xs}px;
  background: var(--color-background-0);
  border: 1px solid var(--color-border-light);
  border-radius: ${SPACING.sm}px;
`;

export const Seg = styled(BaseButton)<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.xs}px;
  min-height: 36px;
  padding: ${SPACING.sm}px ${SPACING.md - SPACING.xs}px;
  border: none;
  border-radius: ${SPACING.sm}px;
  background: ${({ $selected }) =>
    $selected ? "var(--color-card-background)" : "transparent"};
  box-shadow: ${({ $selected }) =>
    $selected ? "var(--shadow-toggle-active, var(--shadow-small))" : "none"};
  color: ${({ $selected }) =>
    $selected ? "var(--color-primary)" : "var(--color-text-secondary)"};
  font-family: inherit;
  font-size: var(--text-base);
  font-weight: ${({ $selected }) =>
    $selected ? "var(--font-weight-bold)" : "500"};
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
    border-color ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
    box-shadow ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
    color ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
    transform ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOutExpo};

  &:hover:not(:disabled) {
    color: var(--color-text-default);
    background: ${({ $selected }) =>
      $selected ? "var(--color-card-background)" : "var(--hover-background)"};
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

// Primary filter row: search, the Type filter, and the "More filters" toggle.
// On small screens search spans the full width while Type and the toggle share
// the line below. From tablet-large up, all three sit on a single row with
// search growing to fill the remaining space.
export const FiltersRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    "search search"
    "type filters";
  gap: ${SPACING.sm + SPACING.xs}px;
  padding-top: ${SPACING.md}px;
  border-top: 1px solid var(--color-border-light);

  & > *:nth-child(1) {
    grid-area: search;
  }

  & > *:nth-child(2) {
    grid-area: type;
  }

  & > *:nth-child(3) {
    grid-area: filters;
    justify-self: end;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    grid-template-columns: minmax(240px, 1fr) auto auto;
    grid-template-areas: "search type filters";
    align-items: center;
    gap: ${SPACING.md}px;

    & > *:nth-child(3) {
      justify-self: auto;
    }
  }
`;

export const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

// Secondary filters revealed by the "More filters" toggle: category, account,
// sort, and the clear action. Kept out of the primary row to lower the number
// of controls competing for attention on first read.
export const AdvancedRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACING.sm}px;
  padding-top: ${SPACING.md}px;
`;

// Count of active advanced filters, shown on the toggle so hidden filters stay
// discoverable when the panel is collapsed.
export const MoreBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 ${SPACING.xs}px;
  border-radius: 999px;
  background: var(--color-primary);
  color: var(--color-card-background);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  line-height: 1;
`;

export const ClearSlot = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
`;

export const ClearFiltersIcon = styled(ClearRounded)`
  font-size: var(--text-lg) !important;
`;

export const DropdownWrapper = styled.div`
  position: relative;
  display: inline-flex;
  min-width: 0;
`;

export const Pill = styled(BaseButton)<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.xs + 2}px;
  min-height: 40px;
  padding: ${SPACING.sm}px ${SPACING.md}px;
  border-radius: ${SPACING.md}px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "var(--color-primary)" : "var(--color-border)"};
  background: ${({ $active }) =>
    $active
      ? "var(--color-card-metric-background)"
      : "var(--color-card-background)"};
  color: ${({ $active }) =>
    $active ? "var(--color-primary)" : "var(--color-text-secondary)"};
  font-family: inherit;
  font-size: var(--text-base);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
    border-color ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
    color ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
    transform ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOutExpo};

  &:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-text-default);
    background: ${({ $active }) =>
      $active
        ? "var(--color-card-metric-background)"
        : "var(--hover-background)"};
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

export const PillValue = styled.span<{ $active: boolean }>`
  font-weight: var(--font-weight-bold);
  color: ${({ $active }) =>
    $active ? "var(--color-primary)" : "var(--color-text-default)"};
`;

export const Chevron = styled(KeyboardArrowDownRounded)<{ $isOpen: boolean }>`
  font-size: var(--text-md) !important;
  transition: transform ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};
  transform: rotate(${({ $isOpen }) => ($isOpen ? "180deg" : "0deg")});
`;

export const Menu = styled.div<{
  $align: "left" | "right";
  $top: number;
  $left: number;
  $right: number;
}>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  ${({ $align, $left, $right }) =>
    $align === "right" ? `right: ${$right}px;` : `left: ${$left}px;`}
  z-index: 2000000;
  min-width: 200px;
  max-height: 280px;
  overflow-y: auto;
  padding: ${SPACING.xs + 2}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-border);
  border-radius: ${SPACING.sm}px;
  box-shadow: var(--shadow-dropdown, var(--shadow-medium));
`;

export const MenuItem = styled(BaseButton)<{ $selected: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: ${SPACING.sm}px ${SPACING.sm + SPACING.xs}px;
  border: none;
  border-radius: ${SPACING.sm}px;
  background: ${({ $selected }) =>
    $selected ? "var(--color-background-2)" : "transparent"};
  color: ${({ $selected }) =>
    $selected ? "var(--color-primary)" : "var(--color-text-default)"};
  font-family: inherit;
  font-size: var(--text-base);
  font-weight: ${({ $selected }) =>
    $selected ? "var(--font-weight-bold)" : "400"};
  cursor: pointer;
  transition:
    background-color ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
    color ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut};

  &:hover {
    background: var(--color-background-2);
  }

  /* Inset ring: the surrounding menu clips an outer one. */
  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
    box-shadow: none;
  }
`;
